import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import AppShell from '@/layouts/AppShell';
import RequireAuth from '@/routes/RequireAuth';

const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const LoginPage = lazy(() => import('@/features/authentication/LoginPage'));
const AuthenticationPage = lazy(() => import('@/features/authentication/AuthenticationPage'));
const EnvironmentsPage = lazy(() => import('@/features/environments/EnvironmentsPage'));
const SearchPage = lazy(() => import('@/features/search/SearchPage'));
const ErrorExplorerPage = lazy(() => import('@/features/error-explorer/ErrorExplorerPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));

// Merchant API — layout + pages loaded together since layout is always needed
const MerchantApiLayout = lazy(() => import('@/features/merchant-api/MerchantApiLayout'));
const MerchantApiOverviewPage = lazy(() => import('@/features/merchant-api/MerchantApiOverviewPage'));
const EndpointPage = lazy(() => import('@/features/merchant-api/EndpointPage'));

// Collection API (merchant-api's hash-authenticated /v1/pg/* Payin endpoints)
const CollectionApiLayout = lazy(() => import('@/features/collection-api/CollectionApiLayout'));
const CollectionApiOverviewPage = lazy(() => import('@/features/collection-api/CollectionApiOverviewPage'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Wrap({ children }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  { path: '/login', element: <Wrap><LoginPage /></Wrap> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      {
        path: '/',
        element: <AppShell />,
        children: [
          { index: true, element: <Wrap><DashboardPage /></Wrap> },
          { path: 'authentication', element: <Wrap><AuthenticationPage /></Wrap> },
          { path: 'environments', element: <Wrap><EnvironmentsPage /></Wrap> },
          { path: 'search', element: <Wrap><SearchPage /></Wrap> },
          { path: 'error-explorer', element: <Wrap><ErrorExplorerPage /></Wrap> },
          { path: 'settings', element: <Wrap><SettingsPage /></Wrap> },
          {
            path: 'merchant-api',
            element: (
              <Wrap>
                <MerchantApiLayout />
              </Wrap>
            ),
            children: [
              {
                index: true,
                element: (
                  <Wrap>
                    <MerchantApiOverviewPage />
                  </Wrap>
                ),
              },
              {
                path: 'endpoint/:endpointId',
                element: (
                  <Wrap>
                    <EndpointPage />
                  </Wrap>
                ),
              },
            ],
          },
          {
            path: 'collection-api',
            element: (
              <Wrap>
                <CollectionApiLayout />
              </Wrap>
            ),
            children: [
              {
                index: true,
                element: (
                  <Wrap>
                    <CollectionApiOverviewPage />
                  </Wrap>
                ),
              },
              {
                path: 'endpoint/:endpointId',
                element: (
                  <Wrap>
                    <EndpointPage />
                  </Wrap>
                ),
              },
            ],
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
